import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { addYears } from 'date-fns';
import { useMutation } from 'react-query';
import {
  useInvoicesForTripCreationSearchParams,
  type Filters,
} from 'hooks/store/invoices/use-invoices-for-trip-creation-search-params';
import {
  useUfOptions,
  useSupplierOptions,
  useCompanyOptions,
  useCarrierOptions,
  useCityOptions,
  useClientOptions,
  useInvoiceIndicatorStatusOptions,
} from 'hooks/api/options';
import { CompanySiteTypeEnum } from 'constants/CompanySiteType';
import { Form, FormField } from 'components/ui/form';
import {
  InputGs,
  ComboboxGs,
  LabelAndCheckbox,
  MultiSelectGs,
  DatePickerGs,
  DateRangePickerGs,
  type SelectContentProps,
} from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { createFilters } from './utils';
import { formSchema, type FormSchema } from './form-schema';

type Ref = {
  resetFilters: () => void;
}

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
};

const SELECTED_BADGE_CLASS_NAME =
  'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]';

export type InvoiceFiltersRef = Ref

export const InvoiceFilters = React.forwardRef<Ref, Props>(({
                                                              onSubmitFilters,
                                                              onResetFilters,
                                                            }, ref) => {
  const { filters } = useInvoicesForTripCreationSearchParams({ persisted: true });
  const ufOptions = useUfOptions();
  const carrierOptions = useCarrierOptions({
    labelType: 'trade-name-and-document',
  });
  const supplierOptions = useSupplierOptions({
    labelType: 'trade-name-and-document',
  });
  const companyOptions = useCompanyOptions({
    labelType: 'trade-name',
    siteType: CompanySiteTypeEnum.DISTRIBUTION_CENTER,
  });
  const clientOptions = useClientOptions({
    combineSameTradeNames: true,
  });
  const statusOptions = useInvoiceIndicatorStatusOptions();
  const { fetchCities } = useCityOptions();

  const [formHydrated, setFormHydrated] = React.useState(false);
  const [originCityOptions, setOriginCityOptions]
    = React.useState<SelectContentProps[]>([]);
  const [destinationCityOptions, setDestinationCityOptions]
    = React.useState<SelectContentProps[]>([]);

  const originCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setOriginCityOptions(data.singleSelectOptions);
    },
  });

  const destinationCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setDestinationCityOptions(data.singleSelectOptions);
    },
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onOriginUfChange = React.useCallback((uf: string) => {
    form.resetField('originIbge');
    originCityOptionsMutation.mutate(uf);
  }, [form]);

  const onOriginUfClear = React.useCallback(() => {
    form.resetField('originIbge');
    setOriginCityOptions([]);
  }, [form]);

  const onDestinationUfChange = React.useCallback((uf: string) => {
    form.resetField('destinationIbge');
    destinationCityOptionsMutation.mutate(uf);
  }, [form]);

  const onDestinationUfClear = React.useCallback(() => {
    form.resetField('destinationIbge');
    setDestinationCityOptions([]);
  }, [form]);

  const hydrateForm = React.useCallback(() => {
    const {
      supplierId,
      companyId,
      carrierId,
      indicatorStatus,
      invoiceNumber,
      emissionDateFrom,
      emissionDateTo,
      deadlineDate,
      sameCarrierCnpj,
      originUf,
      originIbge,
      destinationUf,
      destinationIbge,
    } = filters;

    form.setValue('supplierId', supplierId);
    form.setValue('companyId', companyId);
    form.setValue('carrierId', carrierId);
    form.setValue('indicatorStatus', indicatorStatus);
    form.setValue('invoiceNumber', invoiceNumber ?? '');

    form.setValue('emissionDate', {
      ...(emissionDateFrom && {
        from: new Date(emissionDateFrom + 'T00:00:00'),
      }),
      ...(emissionDateTo && {
        to: new Date(emissionDateTo + 'T23:59:59'),
      }),
    });
    deadlineDate && form.setValue('deadlineDate', new Date(deadlineDate));

    form.setValue('sameCarrierCnpj', sameCarrierCnpj);

    form.setValue('originUf', originUf);
    originUf && onOriginUfChange(originUf);
    form.setValue('originIbge', originIbge);

    form.setValue('destinationUf', destinationUf);
    destinationUf && onDestinationUfChange(destinationUf);
    form.setValue('destinationIbge', destinationIbge);
    setFormHydrated(true);
  }, [
    filters,
    form,
    onOriginUfChange,
    onDestinationUfChange,
  ]);

  const resetForm = React.useCallback(() => {
    form.reset({
      supplierId: undefined,
      companyId: undefined,
      invoiceNumber: '',
      emissionDate: undefined,
      deadlineDate: undefined,
      carrierId: undefined,
      sameCarrierCnpj: false,
      originUf: undefined,
      originIbge: undefined,
      destinationUf: undefined,
      destinationIbge: undefined,
      indicatorStatus: undefined,
    }, { keepValues: false });
    onResetFilters();
  }, [form, onResetFilters]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const filters = createFilters(formData);
    return onSubmitFilters(filters);
  }, [onSubmitFilters]);

  React.useEffect(() => {
    !formHydrated && hydrateForm();
  }, [formHydrated, hydrateForm]);

  React.useImperativeHandle(ref, () => ({
    resetFilters: resetForm,
  }), [resetForm]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className={'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4'}>
          <FormField
            control={form.control}
            name={'supplierId'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={'Fornecedor'}
                maxCount={1}
                options={supplierOptions.multiSelectOptions}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'companyId'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={'Centro'}
                maxCount={1}
                options={companyOptions.multiSelectOptions}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'invoiceNumber'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'NF'}
                       placeholder={'NFs separadas por vírgula'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'emissionDate'}
            render={({ field }) => (
              <DateRangePickerGs
                selected={{
                  from: field.value?.from,
                  to: field.value?.to,
                }}
                onSelect={field.onChange}
                label={'Emissão'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'deadlineDate'}
            render={({ field }) => (
              <DatePickerGs
                {...field}
                label={'Prazo'}
                fieldValue={field.value ? new Date(field.value) : undefined}
                fieldOnChange={field.onChange}
                maxDate={addYears(new Date(), 100)}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'carrierId'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={
                  <LabelAndCheckbox
                    form={form}
                    mainLabel={'Transportadora'}
                    checkBoxLabel={'CNPJ raíz'}
                    checkBoxName={'sameCarrierCnpj'}
                  />
                }
                maxCount={1}
                options={carrierOptions.multiSelectOptions}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'originUf'}
            render={({ field }) => (
              <ComboboxGs
                label={'UF de origem'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={ufOptions.singleSelectOptions}
                onChange={onOriginUfChange}
                onClear={onOriginUfClear}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'originIbge'}
            render={({ field }) => (
              <ComboboxGs
                label={'Origem'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={originCityOptions}
                loading={originCityOptionsMutation.isLoading}
                disabled={!form.getValues().originUf}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'destinationUf'}
            render={({ field }) => (
              <ComboboxGs
                label={'UF de destino'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={ufOptions.singleSelectOptions}
                onChange={onDestinationUfChange}
                onClear={onDestinationUfClear}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'destinationIbge'}
            render={({ field }) => (
              <ComboboxGs
                label={'Destino'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={destinationCityOptions}
                loading={destinationCityOptionsMutation.isLoading}
                disabled={!form.getValues().destinationUf}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'indicatorStatus'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={'Status'}
                maxCount={1}
                options={statusOptions.multiSelectOptions.filter((o) => {
                  return !o.value.startsWith('delivered');
                })}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
        </div>
        <div className={'flex items-center mt-6 gap-4'}>
          <Button.Root
            type={'button'}
            size={'sm'}
            variant={'secondary'}
            onClick={resetForm}
          >
            Limpar filtros
          </Button.Root>
          <Button.Root
            type={'submit'}
            size={'sm'}
            variant={'primary'}
          >
            Filtrar
          </Button.Root>
        </div>
      </form>
    </Form>
  );
});
InvoiceFilters.displayName = 'InvoiceFilters';